import axios from './axios-on-rails';
import Swal from 'sweetalert2';

// Abstract class
// This is the basic functionality for views with translation.
// This class must be extended with specific logic for each view (automatic mail responses, job, company profile, etc).
export default class Translation {
  constructor(rootElementId) {
    this.$scope = $(rootElementId);
    
    // No HTML element was found for this translation object.
    if(this.$scope.length === 0){
      return;
    }

    this.verifyHTML();

    // Bind events for the toggle translation button at the middle of the table header
    this.$scope.find('[data-button-type=toggle-translation-direction]').change(() => {
      this.handleLanguageDirectionToggle();
    });

    // Bind toggle event for language checkboxes at the top of each table header
    ['en', 'ja'].forEach(lang => {
      this.$scope.find(`[data-checkbox-lang=${lang}]`).change(ev => {
        this.handleLanguageCheckboxToggle(ev);
      });
    });

    // Bind content change events for inputs
    this.$scope.on('keyup', '[data-input-lang]', ev => {
      this.handleInputChange(ev.target);
    });

    // Bind click event for translation buttons
    this.$scope.on('click', '[data-button-type=translation]', ev => {
      this.handleTranslationButtonClick(ev.target);
    });

    this.updateAllTranslationButtons();
    this.updateSrcLanguageHiddenField();
    this.updateLanguageDirectionButton();
    this.updateAllInputs();
    this.updateShowNoticeCheckbox();
  }

  // Mostly for debugging and development.
  verifyHTML(){
    if(this.$scope.find("[data-role=translation-src-language]").length === 0){
      throw new Error("Scope must include hidden field for source language");
    }

    if(this.$scope.find("[data-button-type=toggle-translation-direction]").length === 0){
      throw new Error("Scope must include button for toggling source language");
    }

    this.$scope.find(".translation-pair").each((i, ele) => {
      let $ja = $(ele).find("[data-input-lang=ja]");
      let $en = $(ele).find("[data-input-lang=en]");
      if($en.length !== 1 || $ja.length !== 1){
        throw new Error("Each translation pair in the scope must include one Japanese and one English input");
      }
    });
  }

  handleLanguageDirectionToggle(){
    this.updateSrcLanguageHiddenField();
    this.updateAllTranslationButtons();
  }

  handleInputChange(inputEle){
    const $input = this.$scope.find(inputEle);
    if($input.attr('data-input-lang') !== this.srcLang()) return;
    const $translationPair = $input.closest('.translation-pair');
    this.updateOneTranslationButton($translationPair);
  }

  handleLanguageCheckboxToggle(){
    this.updateAllTranslationButtons();
    this.updateLanguageDirectionButton();
    this.updateAllInputs();
    this.updateShowNoticeCheckbox();
  }

  updateShowNoticeCheckbox(){
    const enabled = this.shouldEnableShowNoticeCheckbox();
    this.$scope.find('[data-role=show-automatic-translation-notice]').attr('disabled', !enabled);
  }

  updateAllInputs(){
    this.$scope.find('.translation-pair').each((i, ele) => {
      const $translationPair = $(ele);
      this.updateOneTranslationButton($translationPair);
      const $inputJa = $translationPair.find('[data-input-lang=ja]');
      const $inputEn = $translationPair.find('[data-input-lang=en]');
      const jaEnabled = this.shouldEnableInput('ja');
      const enEnabled = this.shouldEnableInput('en');
      $inputJa.attr('disabled', !jaEnabled);
      $inputEn.attr('disabled', !enEnabled);
    });
  }

  updateOneTranslationButton($translationPair){
    const $inputJa = $translationPair.find('[data-input-lang=ja]');
    const $inputEn = $translationPair.find('[data-input-lang=en]');
    const $translateBtn = $translationPair.find('.translate-btn');
    const srcLang = this.srcLang();
    const inputContent = srcLang === 'ja' ? $inputJa.val().trim() : $inputEn.val().trim();
    const enabled = this.shouldEnableTranslationButton(inputContent);
    $translateBtn.attr('disabled', !enabled);
  }

  updateAllTranslationButtons(){
    // For each translation row where JA and EN fields are present.
    this.$scope.find('.translation-pair').each((i, ele) => {
      const $translationPair = $(ele);
      this.updateOneTranslationButton($translationPair);
    });
  }

  updateLanguageDirectionButton(){
    const $dirButton = this.$scope.find('[data-button-type=toggle-translation-direction]');
    const enabled = this.shouldEnableLanguageDirectionToggleButton();
    this.$scope.find(".translate-head").css('opacity', enabled ? '1' : '.5');
    $dirButton.closest(".translate-arrow").css('opacity', enabled ? '1' : '.5');
    $dirButton.attr('disabled', !enabled);
  }

  // Updates the hidden that contains the source language.
  updateSrcLanguageHiddenField(){
    this.$scope.find("[data-role=translation-src-language]").val(this.srcLang());
  }

  checkboxIsChecked(lang){
    return this.$scope.find(`[data-checkbox-lang=${lang}]`).is(':checked');
  }

  checkboxIsEnabled(lang){
    return !this.$scope.find(`[data-checkbox-lang=${lang}]`).is(":disabled");
  }

  getFormStatus(){

    const srcLang = this.srcLang();
    const destLang = this.destLang();

    return {
      srcLang: srcLang,
      srcLangCheckbox: {
        enabled: this.checkboxIsEnabled(srcLang),
        checked: this.checkboxIsChecked(srcLang)
      },
      destLangCheckbox: {
        enabled: this.checkboxIsEnabled(destLang),
        checked: this.checkboxIsChecked(destLang)
      }
    };
  }

  srcLang(){
    let srcLang = 'en';
    if (this.$scope.find('.js--translate-to-ja').is(':checked')) srcLang = 'ja';
    return srcLang;
  }

  destLang(){
    const srcLang = this.srcLang();
    return srcLang === 'en' ? 'ja' : 'en';
  }

  // return boolean
  showTranslationConfirmDialog($targetElement){
    return $targetElement.val().trim().length > 0;
  }

  handleTranslationButtonClick(buttonEle) {
    const $buttonEle = this.$scope.find(buttonEle);
    $buttonEle.attr('disabled', true);
    const srcLang = this.srcLang();
    const targetLang = srcLang === 'ja' ? 'en' : 'ja';
    const reg = /[0-9]年/gi;
    const $translationPair = $buttonEle.closest(".translation-pair");
    const $sourceElement = $translationPair.find(`[data-input-lang=${srcLang}]`);
    const $targetElement = $translationPair.find(`[data-input-lang=${targetLang}]`);
    let confirmationPromise;

    // Make it an empty promise (resolves providing the same value the confirmation popup would provide)
    // if the target element is empty. Only display the popup if it's not empty.
    if(!this.showTranslationConfirmDialog($targetElement)){
      confirmationPromise = new Promise(r => r({ value: true }));
    } else {
      confirmationPromise = Swal.fire({
        title: gon.translation_dialog.title,
        text: gon.translation_dialog.text,
        icon: false,
        showCancelButton: true,
        confirmButtonText: gon.translation_dialog.confirm,
        cancelButtonText: gon.translation_dialog.cancel
      });
    }

    confirmationPromise.then((result) => {
      if (result.value){
        const editorAttr = $sourceElement.attr("data-editor");
        return this.translate($sourceElement.val(), srcLang, editorAttr === true || editorAttr === "true");
      }
    }).then(res => {
      if(res){
        if($targetElement.attr("data-editor")){
          console.log("editor")
        }
        if(res.data.text.match(reg) !== "null") {
          this.setTranslatedValueToInput($targetElement, res.data.text.replace('年',''));
        } else {
          this.setTranslatedValueToInput($targetElement, res.data.text);
        }
      }
    }).catch(() => {
      alert(gon.translation_dialog.error);
    }).then(() => {
      this.$scope.find(buttonEle).attr('disabled', false);
    });
  }

  setTranslatedValueToInput($targetElement, text){
    $targetElement.val(text);
  }

  translate(srcText, srcLang, useHtml = false) {

    const translateData = {
      src_lang: srcLang,
      text: srcText
    }

    if(useHtml){
      translateData["text"] = srcText
        .replace(/;&/g, "; &")
        .replace(/\n/g, "");

      translateData["text_format"] = "html";
    }

    return axios({
      method: 'post',
      url: Routes.companies_translates_path(),
      data: translateData
    });
  }

  shouldEnableInput(){
    throw new Error("Not implemented");
  }

  shouldEnableTranslationButton(){
    throw new Error("Not implemented");
  }

  shouldEnableLanguageDirectionToggleButton(){
    throw new Error("Not implemented");
  }

  shouldEnableShowNoticeCheckbox(){
    return this.checkboxIsChecked('ja') && this.checkboxIsChecked('en');
  }
}
